import type { SerializedLexicalNode } from 'lexical';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import type { AIActionType } from '../hooks/use-improve-writing.ts';
import type { RichTextInputProps } from './rich-text-input';
import { RichTextInput, type RichTextValue } from './rich-text-input';

type RichTextForm = {
    allowEmpty?: boolean;
    onSubmit: (content: RichTextValue, aiSessionId?: string) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
    initialValue?: RichTextValue | null;
    submitTitle?: ReactNode;
    className?: string;
    style?: React.CSSProperties;
} & Omit<RichTextInputProps, 'value' | 'onChange' | 'onAIInteraction'>;

export const RichTextForm = ({
    allowEmpty = false,
    submitTitle,
    onSubmit,
    onCancel,
    placeholder,
    isSubmitting = false,
    initialValue = null,
    className,
    style,
    ...props
}: RichTextForm) => {
    const { t } = useLocale('common');
    const { currentSessionId } = useVoiceRecording();
    const [content, setContent] = useState<RichTextValue | null>(initialValue);

    const [isAIBusy, setIsAIBusy] = useState<boolean>(false);
    const [aiSessionId, setAISessionId] = useState<string | undefined>(undefined);

    const handleAIInteraction = (action: AIActionType, sessionId: string) => {
        switch (action) {
            case 'accept':
                // Store the session ID for any AI interaction (we'll use it when the note is created)
                setAISessionId(sessionId);
                setIsAIBusy(false);
                break;
            case 'request':
            case 'regenerate':
                setIsAIBusy(true);
                break;
            case 'reject':
                setIsAIBusy(false);
                break;
            default:
                // no-op for unknown actions
                break;
        }
    };

    const handleSubmit = () => {
        if (content && !allowEmpty) {
            onSubmit(content, aiSessionId);
            setContent(null);
        }
    };

    const handleCancel = () => {
        setContent(null);
        onCancel();
    };

    // Recursively checks nested structures (e.g. list → listitem → text)
    const nodeHasText = (node: SerializedLexicalNode): boolean => {
        if ('text' in node && typeof node.text === 'string' && node.text.trim()) {
            return true;
        }
        if ('children' in node && Array.isArray(node.children)) {
            return node.children.some(nodeHasText);
        }
        return false;
    };
    const hasValidContent = content?.root.children.some(nodeHasText);

    const canSubmit = (hasValidContent ?? allowEmpty) && !isSubmitting && !currentSessionId && !isAIBusy;

    return (
        <div className={cn('flex flex-col gap-3', className)} style={style}>
            <div className="rounded-lg bg-white">
                <RichTextInput
                    placeholder={placeholder}
                    value={content}
                    onChange={setContent}
                    onAIInteraction={handleAIInteraction}
                    {...props}
                />
            </div>
            <div className="flex justify-end items-center">
                <div className="flex gap-2">
                    <Button variant="subtle" size="sm" onClick={handleCancel} disabled={isSubmitting}>
                        {t('form__cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        isLoading={isSubmitting}
                    >
                        {submitTitle ?? t('form__submit')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
